import { LocalizationService } from '@abp/ng.core';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CouponDto, CreateCouponDto, UpdateCouponDto } from '../../proxy/coupons/models';
import { CouponService } from '@proxy/coupons';
import { ActivatedRoute, Router } from '@angular/router';
import { SommelierService } from '@proxy/sommeliers';
import { WineService } from '@proxy/wines';
import { SelectOption } from 'src/app/shared/components/metro-form-select/metro-form-select.component';
import { CouponDiscountType, couponDiscountTypeOptions } from '../../proxy/enums/coupon-discount-type.enum';
import { APP_ICONS } from 'src/app/shared/icons/app-icons';
import { ConfirmationModalService } from 'src/app/shared/services/confirmation-modal.service';


@Component({
  selector: 'app-coupon-form',
  templateUrl: './coupon-form.component.html',
  standalone: false
})
export class CouponFormComponent implements OnInit {
  form!: FormGroup;
  isEditMode = false;
  isViewMode = false;
  couponId?: string;
  iconBack = APP_ICONS.arrow;

  discountTypeOptions: SelectOption[] = [];
  sommelierOptions: SelectOption[] = [];
  wineOptions: SelectOption[] = [];
  clientOptions: SelectOption[] = [];

  constructor(
    private fb: FormBuilder,
    private couponService: CouponService,
    private sommelierService: SommelierService,
    private wineService: WineService,
    private route: ActivatedRoute,
    private router: Router,
    private localizationService: LocalizationService,
    private confirmation: ConfirmationModalService
  ) {
    this.buildForm();
  }

  ngOnInit(): void {
    this.initOptions();
    this.loadSommeliers();
    this.loadWines();
    this.loadClients();

    this.isViewMode = this.router.url.includes('/view/');
    this.couponId = this.route.snapshot.paramMap.get('id');

    if (this.couponId) {
      if (!this.isViewMode) {
        this.isEditMode = true;
      }
      
      this.couponService.get(this.couponId).subscribe(coupon => {
        this.patchForm(coupon);
        if (this.isViewMode) {
          this.form.disable();
        }
      });
    }

    // Uppercase transformation for code
    this.form.get('code')?.valueChanges.subscribe(value => {
      if (value) {
        this.form.get('code')?.setValue(value.toUpperCase(), { emitEvent: false });
      }
    });
  }

  initOptions() {
    this.discountTypeOptions = [
      { value: CouponDiscountType.Percentage, label: this.localizationService.instant('::Percentage') },
      { value: CouponDiscountType.Fixed, label: this.localizationService.instant('::Fixed') }
    ];
  }

  private buildForm(): void {
    const now = new Date();
    const localIsoString = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().substring(0, 16);

    this.form = this.fb.group({
      code: ['', [Validators.required, Validators.pattern(/^[A-Z0-9]+$/)]],
      discountType: [CouponDiscountType.Percentage, [Validators.required]],
      discountValue: [0, [Validators.required, Validators.min(1)]],
      startDate: [localIsoString, [Validators.required]],
      endDate: ['', [Validators.required]],
      maxUses: [null, [Validators.min(1)]],
      assignedToSommelierId: [null],
      sommelierIds: [[]],
      wineIds: [[]],
      clientIds: [[]],
      isActive: [true],
      minimumAmount: [null, [Validators.min(0)]]
    });

    // Dynamic validation for discount value
    this.form.get('discountType')?.valueChanges.subscribe(type => {
      const valueControl = this.form.get('discountValue');
      if (type === CouponDiscountType.Percentage) {
        valueControl?.setValidators([Validators.required, Validators.min(1), Validators.max(100)]);
      } else {
        valueControl?.setValidators([Validators.required, Validators.min(1)]);
      }
      valueControl?.updateValueAndValidity();
    });
  }

  private patchForm(coupon: CouponDto) {
    this.form.patchValue({
      ...coupon,
      startDate: coupon.startDate ? coupon.startDate.substring(0, 16) : '',
      endDate: coupon.endDate ? coupon.endDate.substring(0, 16) : '',
      sommelierIds: coupon.sommelierIds || [],
      wineIds: coupon.wineIds || [],
      clientIds: coupon.clientIds || [],
      minimumAmount: coupon.minimumAmount || null
    });
  }

  loadSommeliers() {
    this.sommelierService.getList({ maxResultCount: 1000, status: 'Approved' } as any).subscribe(result => {
      this.sommelierOptions = result.items.map(s => ({ 
        label: s.fullName || `${s.name} ${s.surname}` || s.userName, 
        value: s.id 
      })).sort((a, b) => a.label.localeCompare(b.label));
    });
  }

  loadWines() {
    this.wineService.getList({ maxResultCount: 1000 } as any).subscribe(result => {
      this.wineOptions = result.items.map(w => ({ 
        label: w.name, 
        value: w.id 
      })).sort((a, b) => a.label.localeCompare(b.label));
    });
  }

  loadClients() {
    this.couponService.getClientLookup().subscribe(result => {
      this.clientOptions = result.map(c => ({
        label: c.displayName,
        value: c.id
      }));
    });
  }

  getErrorMessage(controlName: string): string | undefined {
    const control = this.form.get(controlName);
    if (control?.invalid && control?.touched) {
      if (control.errors?.['required']) return '::ThisFieldIsRequired';
      if (control.errors?.['pattern']) return '::OnlyUppercaseAlphanumeric';
      if (control.errors?.['min']) return '::ValueTooLow';
      if (control.errors?.['max']) return '::ValueTooHigh';
    }
    return undefined;
  }

  onSubmit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const formValue = this.form.getRawValue();
    const dto = {
      ...formValue,
      assignedToSommelierId: formValue.assignedToSommelierId || null,
      sommelierIds: formValue.sommelierIds || [],
      wineIds: formValue.wineIds || [],
      clientIds: formValue.clientIds || [],
      maxUses: formValue.maxUses || null,
      minimumAmount: formValue.minimumAmount || null
    };
    
    if (this.isEditMode && this.couponId) {
      this.couponService.update(this.couponId, dto as UpdateCouponDto).subscribe(() => this.handleSuccess());
    } else {
      this.couponService.create(dto as CreateCouponDto).subscribe(() => this.handleSuccess());
    }
  }

  private handleSuccess() {
    this.router.navigate(['/coupons']);
  }

  cancel() {
    this.router.navigate(['/coupons']);
  }

  deleteCoupon() {
    if (!this.couponId) return;
    
    const code = this.form.get('code')?.value;
    
    this.confirmation.confirm({
      title: 'Eliminar cupón',
      message: `¿Estás seguro de que deseas eliminar el cupón ${code}?`,
      type: 'danger',
      showCancelButton: true,
    }).subscribe((confirmed) => {
      if (!confirmed) return;
      this.couponService.delete(this.couponId!).subscribe(() => {
        this.router.navigate(['/coupons']);
      });
    });
  }
}
